import { forwardRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ActionIcon, Box, createStyles, Loader, MultiSelect, NumberInput, Select, Text, Textarea, TextInput } from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconCheck, IconChevronDown, IconPhotoPlus, IconX } from "@tabler/icons-react";
import { LuCalendar } from "react-icons/lu";
import { getAllPatientsModal } from "../../services/patient";
import { getAllStaff } from "../../services/staff";
import { getAllFacilitiesModal } from "../../services/facility";
import { getIssueCategories } from "../../services/serviceRequests";
import { uploadFileToS3Api } from "../../utils/PresignedUrlFunction";
import { evaluateFormula, fieldSpan } from "../ServiceRequestTemplateBuilder/schema";
import { decrypt, isServiceRequestAdmin, showError } from "../../utils";

// Every selector in the mockup carries the same single arrow-down caret
// (`.dd-btn > svg` / `.ms-caret`). Mantine's own default rightSection is a
// double (up+down) chevron and differs per input type besides, so a recolor of
// "whatever's there" isn't enough — each Select/MultiSelect gets this caret
// explicitly. pointer-events stay off so clicking the icon still opens the
// dropdown, exactly as Mantine's own default section does.
export const srCaretProps = (variant, Icon = IconChevronDown) => ({
  rightSection: <Icon size={variant === "inline" ? 14 : 16} />,
  rightSectionProps: { style: { pointerEvents: "none", color: "var(--app-text-muted)" } },
});

// A `clearable` field keeps Mantine's clear button once it holds a value —
// overriding the right section there would take clearing away, so the caret
// (or, for DateField, the calendar glyph) only stands in while the field is
// empty.
const caretUnlessClearable = (variant, value, Icon) => (value ? {} : srCaretProps(variant, Icon));

const useFieldStyles = createStyles(() => ({
  // Mockup `#drawer-new .field label`: 13px/600, 6px below, no uppercase.
  label: { fontFamily: "Inter", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--app-text)" },
  // Mockup `#drawer-new .ctl` / `.dd-btn`: 44px tall, 10px/14px padding, 14px
  // text, amber border + 3px amber ring on focus.
  input: {
    minHeight: 44,
    borderRadius: 8,
    padding: "10px 14px",
    fontFamily: "Inter",
    fontSize: 14,
    lineHeight: 1.5,
    borderColor: "var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    "&::placeholder": { color: "var(--app-text-muted)" },
    "&:focus, &:focus-within": {
      borderColor: "var(--app-primary)",
      boxShadow: "0 0 0 3px rgba(255, 175, 74, 0.2)",
    },
    "&:disabled": { background: "var(--app-surface-2)", color: "var(--app-text-muted)", opacity: 1 },
  },
  // Mockup `.ms-box`: the chips sit tighter than a plain control's text.
  inputMulti: { padding: "5px 34px 5px 8px" },
  // Mockup `#drawer-new textarea.ctl`: taller, slightly smaller, looser lines.
  inputArea: { minHeight: 110, fontSize: 13, lineHeight: 1.55 },
  required: { color: "#DC2626" },
  // "inline" variant — matches the mockup's rail fields (`.rail-k`/`.rail-v`/
  // `.dd-btn-inline`): a field that reads as plain label+value text until
  // touched, not a boxed form control. Used by the Detail drawer, where
  // every field is directly editable and a wall of bordered inputs would
  // read as a totally different, form-like screen instead of the same
  // request view the mockup shows.
  labelInline: {
    fontFamily: "Inter", fontSize: 10, fontWeight: 700, letterSpacing: ".05em",
    textTransform: "uppercase", color: "var(--app-text-muted)", marginBottom: 4,
  },
  // `root` is Input.Wrapper's own box — the one that actually contains
  // BOTH the label and the input row, so it's the one that has to be
  // flex-column for the label to stack above the value. `wrapper` (below)
  // only wraps the input+icon+rightSection row itself; styling that alone
  // left label and value sitting side by side, since nothing was forcing
  // the label above it in the first place.
  rootInline: { display: "inline-flex", flexDirection: "column", alignItems: "flex-start", width: "auto", maxWidth: "100%" },
  // A chip tray still needs the label stacked above it, but stays full
  // width — the fit-content root above is only for a single, short value.
  rootInlineMulti: { display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%" },
  // Keeps the input row itself fit-content too, so it doesn't stretch to
  // fill whatever width `root` ends up with.
  wrapperInline: { display: "inline-flex", width: "auto", maxWidth: "100%" },
  // Recolors Mantine's own default chevron/caret icon rather than swapping
  // it out — matches the mockup's muted `.rv-caret` without needing a
  // custom rightSection on every single field.
  rightSectionInline: { color: "var(--app-text-muted)", opacity: 0.55, pointerEvents: "none" },
  inputInline: {
    width: "auto",
    minHeight: 26,
    height: "auto",
    borderRadius: 6,
    padding: "3px 7px",
    margin: "-3px -7px",
    fontFamily: "Inter",
    fontSize: 13.5,
    fontWeight: 600,
    lineHeight: 1.35,
    border: "1px solid transparent",
    background: "transparent",
    color: "var(--app-text)",
    "&::placeholder": { color: "var(--app-text-muted)" },
    "&:hover": { background: "var(--app-surface-2)", borderColor: "var(--app-border)" },
    "&:focus, &:focus-within": {
      borderColor: "var(--app-primary)",
      background: "var(--app-surface)",
      boxShadow: "0 0 0 3px rgba(255, 175, 74, 0.2)",
    },
    "&:disabled": { background: "transparent", color: "var(--app-text-muted)", opacity: 1 },
  },
  // Mockup's rail multi-select (`.rms-box`) keeps a real box even inline —
  // a chip tray reads as empty/broken without a boundary, unlike a plain
  // single value that can just sit as text.
  inputInlineMulti: {
    minHeight: 38,
    borderRadius: 8,
    // Right padding reserves the caret's column (`.rms-caret`) so chips can't
    // slide underneath it — Mantine lays the right section over the input.
    padding: "5px 34px 5px 10px",
    fontFamily: "Inter",
    fontSize: 13,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    "&::placeholder": { color: "var(--app-text-muted)" },
    "&:focus-within": {
      borderColor: "var(--app-primary)",
      boxShadow: "0 0 0 3px rgba(255, 175, 74, 0.2)",
    },
  },
  // Select / MultiSelect dropdown popover — matches the mockup's .dd-pop/.dd-opt.
  dropdown: {
    borderRadius: 10,
    border: "1px solid var(--app-border)",
    boxShadow: "0 8px 24px rgba(16,24,40,.14)",
    padding: 5,
  },
  // DateField's calendar popover doesn't read the `dropdown` classNames key
  // above (Mantine's DateInput renders its own Popover.Dropdown outside the
  // classNames it forwards to the Calendar) — styled separately via
  // `popoverProps.classNames.dropdown`. Matches the mockup's `.dd-menu.cal`.
  dateDropdown: {
    borderRadius: 10,
    border: "1px solid var(--app-border)",
    boxShadow: "0 8px 24px rgba(16,24,40,.14)",
    padding: 10,
  },
  // Mockup `.cal-head`: centered, bold, 13px, 8px clearance to the grid below.
  // The mockup's calendar is a static prototype with no month navigation, but
  // removing the real prev/next controls here would be a functional
  // regression, not just a style one — so they stay, just recolored to sit
  // quietly beside the centered label instead of reading as separate buttons.
  calHeader: { marginBottom: 8 },
  calHeaderLevel: { fontSize: 13, fontWeight: 700, color: "var(--app-text)", textTransform: "none" },
  calHeaderControl: { color: "var(--app-text-muted)" },
  // Mockup `.cal-dow`: 10px/700 uppercase, muted.
  calWeekday: { fontSize: 10, fontWeight: 700, color: "var(--app-text-muted)", textTransform: "uppercase", padding: "3px 0" },
  // Mockup `.cal-day`: 28px square, 12px tabular-nums, plain until hovered/
  // selected. `!important` on size/color because Mantine's own Day styles
  // set the same properties in a same-specificity rule, and which one wins
  // on a tie depends on injection order rather than anything this class
  // controls.
  calDay: {
    height: "28px !important",
    width: "28px !important",
    fontSize: "12px !important",
    color: "var(--app-text) !important",
    borderRadius: 6,
    fontVariantNumeric: "tabular-nums",
    "&:hover": { backgroundColor: "var(--app-surface-2)" },
    "&[data-weekend]": { color: "var(--app-text) !important" },
    "&[data-outside]": { color: "var(--app-text-muted) !important" },
    "&[data-selected], &[data-selected]:hover": {
      backgroundColor: "var(--app-primary) !important",
      color: "var(--app-text) !important",
      fontWeight: 700,
    },
  },
  // Mockup's day grid is a tight 2px gap; Mantine lays days out as table
  // cells with 8px of padding-as-gutter instead, so the gutter is trimmed
  // here rather than there being an actual `gap` to set.
  calMonthCell: { "&[data-with-spacing]": { padding: "1px !important" } },
  // The table's own natural width is just 7 × day-size — narrower than the
  // popover once that's pinned to the target field's width — so it's
  // stretched to fill the dropdown rather than leaving dead space beside it.
  calMonth: { width: "100%" },
  item: {
    borderRadius: 7,
    fontFamily: "Inter",
    fontSize: 13,
    padding: "8px 10px",
    color: "var(--app-text)",
    "&[data-hovered]": { backgroundColor: "var(--app-surface-2)" },
    "&[data-selected], &[data-selected]:hover": { backgroundColor: "var(--app-tint-amber)", color: "var(--app-ink-amber)", fontWeight: 600 },
  },
  // MultiSelect selected-item chip — matches the mockup's `#drawer-new
  // .ms-chip`: 16px pill, 3px/4px/3px/11px padding, 13px text.
  chip: {
    height: "auto",
    borderRadius: 16,
    padding: "3px 4px 3px 11px",
    fontFamily: "Inter",
    fontSize: 13,
  },
  value: {
    backgroundColor: "var(--app-tint-amber)",
    border: "1px solid var(--app-primary)",
    color: "var(--app-ink-amber)",
  },
  defaultValueRemove: { color: "var(--app-ink-amber)" },

  // `.ms-chip.staff` — staff read as a neutral slate pill, not brand amber.
  valueStaff: {
    backgroundColor: "#F1F5F9",
    border: "1px solid #E2E8F0",
    color: "#334155",
  },
  defaultValueRemoveStaff: { color: "#334155" },

  severityDot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block", flexShrink: 0 },

  // The template studio previews every choice field as a row of toggle
  // buttons (`.pvseg` in studioStyle) — mirrored here so what an author
  // previews is what a submitter is handed.
  seg: { display: "flex", gap: 8, flexWrap: "wrap" },
  segBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    border: "1px solid var(--app-border)", background: "var(--app-surface)", borderRadius: 8,
    padding: "9px 18px", fontFamily: "Inter", fontSize: 13, fontWeight: 600, color: "var(--app-text-muted)", cursor: "pointer",
    "&:hover": { borderColor: "var(--app-text-muted)" },
  },
  // Matches the mockup's `.rq-seg button[aria-pressed=true]` exactly — a
  // solid dark/"ink" fill, not the brand amber tint used for other
  // selected states in this module.
  segBtnOn: {
    background: "var(--app-text)", borderColor: "var(--app-text)",
    color: "#fff", fontWeight: 600,
  },
  segHelp: { fontSize: 12, color: "var(--app-text-muted)", marginTop: 6 },
  // `.pvcalc` — a computed total reads as a result, not as another empty box
  // waiting for input.
  calcBox: {
    minHeight: 42, border: "1px solid #CDEBD9", background: "#ECFDF3", borderRadius: 8,
    display: "flex", alignItems: "center", padding: "0 12px",
    fontSize: 15, fontWeight: 700, color: "#027A48",
  },
  calcBoxEmpty: { fontSize: 13, fontWeight: 500, color: "var(--app-text-muted)", background: "var(--app-surface-2)", borderColor: "var(--app-border)" },
  // Mockup priority indicator (prioDots()) is a monochrome 3-bar rank
  // indicator — not a per-priority color swatch.
  prioDotRow: { display: "inline-flex", alignItems: "center", gap: 3, marginRight: 7 },
  prioDot: { width: 6, height: 6, borderRadius: "50%", background: "transparent", border: "1.5px solid var(--app-text-muted)", boxSizing: "border-box", display: "inline-block", flexShrink: 0 },
  prioDotOn: { background: "var(--app-text)", borderColor: "var(--app-text)" },
  // Mockup `.dd-opt`: dots+label on the left, a checkmark (`.chk`) pinned to
  // the right — always in the DOM so the row's width doesn't jump, hidden via
  // opacity except on the selected row.
  prioItem: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  // Mockup `.dd-opt.sel` only goes bold — it never recolors the row (that's
  // the shared `.item` class's own selected state, which the checkmark
  // already carries here instead).
  prioItemNoColor: { "&[data-selected], &[data-selected]:hover": { color: "var(--app-text) !important" } },
  prioCheck: { flexShrink: 0, color: "var(--app-primary)", opacity: 0 },
  prioCheckOn: { opacity: 1 },
  // Mockup `#drawer-new .dd-btn`: 14px left/right padding either side of the
  // dots+label icon slot and the caret's right-section slot. Both Mantine
  // slots default to centering their content in a same-specificity padding
  // rule, so both get forced here rather than left to the shared `input`
  // class to fight over injection order.
  prioIconSlot: { justifyContent: "flex-start !important", paddingLeft: "14px !important" },
  prioRightSlot: { justifyContent: "flex-end !important", paddingRight: "14px !important" },
  prioInputPad: { paddingLeft: "45px !important", paddingRight: "30px !important" },
  // Mockup `#drawer-new .dropwrap`: dashed 10px card, stacked icon + two
  // lines of copy, amber wash on hover.
  photoDropzone: {
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: "var(--app-border)",
    background: "var(--app-surface-2)",
    padding: "22px 16px",
    "&:hover": { borderColor: "var(--app-primary)", background: "var(--app-tint-amber)" },
  },
  dropInner: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, pointerEvents: "none" },
  dropIcon: {
    width: 42,
    height: 42,
    borderRadius: 999,
    background: "var(--app-tint-amber)",
    color: "var(--app-ink-amber)",
    display: "grid",
    placeItems: "center",
    marginBottom: 4,
  },
  dropMain: { fontSize: 13.5, fontWeight: 500, color: "var(--app-text)" },
  dropBrand: { color: "var(--app-ink-amber)", fontWeight: 700 },
  dropSub: { fontSize: 12, color: "var(--app-text-muted)" },
  photoGrid: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 },
  photoThumbWrap: { position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid var(--app-border)" },
  photoThumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  photoRemove: {
    position: "absolute",
    top: 3,
    right: 3,
    background: "rgba(0,0,0,.55)",
    color: "#fff",
    "&:hover": { background: "rgba(0,0,0,.75)" },
  },
  // Grid-span helpers for dynamic template fields inside a 12-col .dgrid —
  // one per width the template builder's Width picker offers (⅓ / ½ / Full).
  // A template author's ⅓ and ½ widths are a statement about a desktop form —
  // held literally on a phone they'd leave a date picker or a chip tray in a
  // 110px cell. Both collapse to full width there; ⅓ takes a half-step at
  // tablet width first, since three-across is the layout that breaks earliest.
  span12: { gridColumn: "span 12" },
  span6: { gridColumn: "span 6", "@media (max-width: 640px)": { gridColumn: "span 12" } },
  span4: {
    gridColumn: "span 4",
    "@media (max-width: 860px)": { gridColumn: "span 6" },
    "@media (max-width: 640px)": { gridColumn: "span 12" },
  },

  // Plain "default"-variant buttons across the module — matches the
  // mockup's .btn (Cancel/Reports/Edit/outline actions etc.).
  btn: {
    height: 34,
    padding: "0 13px",
    borderRadius: 8,
    borderColor: "var(--app-border)",
    backgroundColor: "var(--app-surface)",
    color: "var(--app-text)",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    "&:hover": { backgroundColor: "var(--app-surface-2)" },
  },
}));

// Shared classNames map so every Mantine form control in the module (New
// Request drawer, Detail drawer edit mode) renders with identical field
// chrome — matches the mockup's .field/.rq-ctl/.dd-pop look via Mantine's own
// classNames API instead of the styles={{...}} prop.
export const useSrFieldClassNames = (variant = "card") => {
  const { classes, cx } = useFieldStyles();
  const inline = variant === "inline";
  return {
    ...(inline ? { root: classes.rootInline, wrapper: classes.wrapperInline, rightSection: classes.rightSectionInline } : {}),
    label: inline ? classes.labelInline : classes.label,
    input: inline ? classes.inputInline : classes.input,
    required: classes.required,
    dropdown: classes.dropdown,
    item: classes.item,
    value: cx(classes.chip, classes.value),
    defaultValueRemove: classes.defaultValueRemove,
  };
};

export const useSrStaffFieldClassNames = (variant = "card") => {
  const { classes, cx } = useFieldStyles();
  const inline = variant === "inline";
  return {
    ...(inline ? { root: classes.rootInline, wrapper: classes.wrapperInline, rightSection: classes.rightSectionInline } : {}),
    label: inline ? classes.labelInline : classes.label,
    input: inline ? classes.inputInline : classes.input,
    required: classes.required,
    dropdown: classes.dropdown,
    item: classes.item,
    value: cx(classes.chip, classes.valueStaff),
    defaultValueRemove: classes.defaultValueRemoveStaff,
  };
};

// A MultiSelect's box packs chips instead of text (`.ms-box`), and a textarea
// is taller with looser lines (`textarea.ctl`) — both keep every other piece
// of the shared field chrome.
export const useSrMultiClassNames = (base, variant = "card") => {
  const { classes, cx } = useFieldStyles();
  // A chip tray needs its own row regardless — the fit-content wrapper a
  // plain inline Select gets would just clip it.
  if (variant === "inline") return { ...base, root: classes.rootInlineMulti, wrapper: undefined, input: classes.inputInlineMulti };
  return { ...base, input: cx(base.input, classes.inputMulti) };
};

export const useSrAreaClassNames = (base) => {
  const { classes, cx } = useFieldStyles();
  return { ...base, input: cx(base.input, classes.inputArea) };
};

// Shared className for a plain "default"-variant Mantine <Button> — matches
// the mockup's generic .btn (Cancel, Reports, Edit, outline actions, ...).
export const useSrButtonClassNames = () => {
  const { classes } = useFieldStyles();
  return classes.btn;
};

// Rank used by the mockup's prioDots() bar: 1/2/3 filled dots for Low/Medium/High.
const PRIORITY_RANK = { Low: 1, Medium: 2, High: 3 };
// Exported for the template studio's preview, which has to offer the same
// choices this field does or it isn't previewing the real form.
export const PRIORITY_LEVELS = Object.keys(PRIORITY_RANK);

const PrioDots = ({ priority, dotRowClass, dotClass, dotOnClass }) => {
  const rank = PRIORITY_RANK[priority] || 0;
  return (
    <span className={dotRowClass}>
      {[1, 2, 3].map((i) => (
        <span key={i} className={`${dotClass} ${i <= rank ? dotOnClass : ""}`} />
      ))}
    </span>
  );
};

const PrioritySelectItem = forwardRef(
  ({ label, selected, dotRowClass, dotClass, dotOnClass, itemClass, checkClass, checkOnClass, className, ...others }, ref) => (
    <Box ref={ref} {...others} className={`${className || ""} ${itemClass}`}>
      <span style={{ display: "flex", alignItems: "center" }}>
        <PrioDots priority={label} dotRowClass={dotRowClass} dotClass={dotClass} dotOnClass={dotOnClass} />
        <span>{label}</span>
      </span>
      <IconCheck size={16} className={`${checkClass} ${selected ? checkOnClass : ""}`} />
    </Box>
  )
);

// Title / Priority / Issue Category are grouped under a "Request" card, and
// Client(s) / Staff under a "Details" card, matching the mockup's drawer-new
// section layout (Details card, then Request card).
export const TitleField = ({ value, onChange, variant }) => {
  const cn = useSrFieldClassNames(variant);
  return (
    <TextInput
      label="Title"
      required
      mb={14}
      classNames={cn}
      placeholder="Short summary…"
      value={value || ""}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
};

export const PriorityField = ({ value, onChange, variant }) => {
  const cn = useSrFieldClassNames(variant);
  const { classes, cx } = useFieldStyles();
  // Only the boxed "card" control (the mockup's `.dd-btn`, 44px tall with a
  // 14px inset either side) needs its icon/right-section slots pinned to an
  // exact width — the compact "inline" rail control (`.dd-btn-inline`) sizes
  // itself around its content instead, so it's left on Mantine's own default.
  const boxed = variant !== "inline";
  return (
    <Select
      label="Priority"
      required
      mb={14}
      classNames={boxed ? { ...cn, input: cx(cn.input, classes.prioInputPad), icon: classes.prioIconSlot, rightSection: classes.prioRightSlot } : cn}
      data={PRIORITY_LEVELS}
      itemComponent={(props) => (
        <PrioritySelectItem
          {...props}
          dotRowClass={classes.prioDotRow}
          dotClass={classes.prioDot}
          dotOnClass={classes.prioDotOn}
          itemClass={cx(classes.prioItem, classes.prioItemNoColor)}
          checkClass={classes.prioCheck}
          checkOnClass={classes.prioCheckOn}
        />
      )}
      icon={value ? <PrioDots priority={value} dotRowClass={classes.prioDotRow} dotClass={classes.prioDot} dotOnClass={classes.prioDotOn} /> : null}
      iconWidth={value && boxed ? 45 : undefined}
      {...srCaretProps(variant)}
      rightSectionWidth={boxed ? 30 : undefined}
      value={value || "Medium"}
      onChange={onChange}
    />
  );
};

export const IssueCategoryField = ({ value, onChange, issueCategories, variant }) => {
  const cn = useSrFieldClassNames(variant);
  return (
    <Select
      label="Issue Category"
      required
      mb={14}
      classNames={cn}
      placeholder="Select a category…"
      data={(issueCategories || []).filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }))}
      {...srCaretProps(variant)}
      value={value || ""}
      onChange={onChange}
    />
  );
};

// Shared directory fetch for the Client(s)/Staff/Facility pickers below —
// same "fetch all, guarded by what's already loaded" idiom used by every
// other module's dropdown (e.g. Scheduler's MissedShiftDrawer). Patient
// names come back already decrypted by getAllPatientsModal itself (it
// decrypts client-side before dispatch), so callers never touch ciphertext.
const useDirectories = () => {
  const allPatients = useSelector((state) => state.patient?.allPatients) || [];
  const allStaff = useSelector((state) => state.staff?.allStaff) || [];
  const allFacilities = useSelector((state) => state.facility?.allFacilities?.facilities) || [];
  useEffect(() => {
    if (!allPatients.length) getAllPatientsModal("all", false);
    if (!allStaff.length) getAllStaff("all", false);
    if (!allFacilities.length) getAllFacilitiesModal("all", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { allPatients, allStaff, allFacilities };
};

// The org's issue categories (Settings → Issue Categories), for the built-in
// picker and for a template's own Issue Category field. Same lazy-fetch idiom
// as useDirectories: whichever screen opened the form may not have loaded
// them, and a template field can't assume it did.
//
// Options are keyed by NAME, not id: a template field's answer lands in the
// request's loose `fieldValues` map, which every consumer (detail drawer, PDF
// report) prints verbatim with no lookup to resolve an id against. Storing the
// name also means free text captured before this was a picker still matches
// its category.
const useIssueCategoryOptions = (activeOnly) => {
  const categories = useSelector((state) => state.serviceRequests?.issueCategories) || [];
  useEffect(() => {
    if (!categories.length) getIssueCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return categories
    .filter((c) => (activeOnly === false ? true : c.active))
    .map((c) => ({ value: c.name, label: c.name }));
};

export const ClientsField = ({ value, onChange, variant }) => {
  const cn = useSrMultiClassNames(useSrFieldClassNames(variant), variant);
  const { allPatients } = useDirectories();
  const options = allPatients
    .filter((p) => p?.active !== false && p?.deleted !== true)
    .map((p) => ({ value: p.id, label: p.name }));
  return (
    <MultiSelect
      label="Client Name(s)"
      mb={14}
      classNames={cn}
      placeholder="Select client(s)…"
      data={options}
      value={value || []}
      searchable
      onChange={onChange}
      {...srCaretProps(variant)}
    />
  );
};

const staffName = (s) => s?.name || [s?.firstName, s?.lastName].filter(Boolean).join(" ") || "Unnamed";

// Linked Skypoint contractors ride in the SAME staff directory as the org's own
// staff — the /staff route resolves the admin's `contractorLinks[]` and unshifts
// each one into `staffs`/`allStaff`, stamped `role: "Contractor"` (their own
// account keeps `type: "contractor"`). Checked on both fields because which one
// is set depends on whether the record came through that unshift or straight
// out of the directory query.
const isContractorRecord = (s) => ["type", "role"]
  .some((k) => String(s?.[k] || "").toLowerCase() === "contractor");

// Who may be named on a request: the org's own active staff only. Contractors
// share the directory but are not org staff, so they're excluded here the same
// way MobileChart's incident staff field excludes them — otherwise they'd show
// up as candidates simply because the /staff response happens to carry them.
const isSelectableStaff = (s) => !!s
  && s.active !== false
  && s.deleted !== true
  && !isContractorRecord(s);

export const StaffField = ({ value, onChange, variant }) => {
  const cn = useSrMultiClassNames(useSrStaffFieldClassNames(variant), variant);
  const { allStaff } = useDirectories();
  const me = useSelector((state) => state.profile?.profile?.user);
  const options = allStaff
    .filter(isSelectableStaff)
    .map((s) => ({ value: String(s.id || s._id), label: staffName(s) }));
  // The submitter is pre-selected (see the New Request drawer), so they must
  // always be an option — admins don't necessarily appear in the staff
  // directory, and MultiSelect silently drops a value it has no option for.
  const myId = String(me?.id || me?._id || "");
  if (myId && !options.some((o) => o.value === myId)) options.unshift({ value: myId, label: staffName(me) });
  return (
    <MultiSelect
      label="Staff Name(s)"
      mb={14}
      classNames={cn}
      placeholder="Select staff…"
      data={options}
      value={value || []}
      searchable
      onChange={onChange}
      {...srCaretProps(variant)}
    />
  );
};

// Built-in Details/Request fields that show for every request type (mirrors the
// mockup's fixed Facility/Shift/Date/Time/Description), independent of the template.
export const SHIFTS = ["Morning (7 AM to 3 PM)", "Evening (3 PM to 11 PM)", "Night (11 PM to 7 AM)"];

export const refId = (v) => (typeof v === "string" ? v : v?.id || v?._id);

// A populated reference (user / facility / template / …) as a label. Falls
// back to the raw id so a row never renders blank when a populate is missing.
export const displayRef = (val) => {
  if (!val) return "—";
  if (typeof val === "string") return val;
  return val.name || val.title || [val.firstName, val.lastName].filter(Boolean).join(" ") || val.id || "—";
};

/**
 * Same as displayRef, for a Patient. Patient.name is stored encrypted and the
 * API hands back the ciphertext verbatim, so anything reading a *populated*
 * client off a request has to decrypt it — the patient directory in Redux is
 * already decrypted (getAllPatientsModal does it before dispatch), but a
 * request's own `clients[]` is not.
 */
export const clientRefName = (val) => {
  if (!val) return "—";
  if (typeof val === "string") return val;
  const raw = val.name;
  if (!raw) return displayRef(val);
  return decrypt(raw, false) || raw;
};

/**
 * The facilities the current user may file against, before any client narrowing.
 * A staff member works at the sites they're assigned to (profile.user.facilityId,
 * already populated by GET /me), so those are the only ones they can attribute a
 * request to — otherwise a request lands on a facility the submitter has nothing
 * to do with. Admins and the account owner administer the whole org and keep
 * every facility; they also edit other people's requests in the detail drawer,
 * where the facility may legitimately be any of the org's.
 *
 * Same admin-vs-staff split the rest of the module uses (isServiceRequestAdmin),
 * reading the profile's own facility list exactly as the check-in facility picker
 * does (SelectCheckInType).
 *
 * A staff account with no assignment at all keeps the full list: Facility is
 * required to submit, so narrowing to nothing would leave them unable to file a
 * request — an org that simply doesn't assign facilities must not be locked out.
 */
const facilitiesForUser = (active, profile) => {
  if (isServiceRequestAdmin(profile)) return active;
  const mine = new Set((profile?.user?.facilityId || []).map(refId).filter(Boolean));
  if (!mine.size) return active;
  return active.filter((f) => mine.has(refId(f)));
};

/**
 * Facilities a request can belong to. Starts from what the current user is
 * allowed to file against (facilitiesForUser), then narrows further by client:
 * clients are assigned to facilities (Patient.facilityId[]), so as soon as any
 * client is on the request the choice drops to the facilities that actually serve
 * them — you can't file a request against a site the client isn't at. With no
 * client picked yet, the user's own facilities are offered.
 */
export const useScopedFacilities = (clientIds) => {
  const { allFacilities, allPatients } = useDirectories();
  const profile = useSelector((state) => state.profile?.profile);
  const active = facilitiesForUser(
    (allFacilities || []).filter((f) => f?.active !== false && f?.deleted !== true),
    profile
  );
  const picked = (clientIds || []).filter(Boolean);
  if (!picked.length) return { options: active.map((f) => ({ value: f.id, label: f.name })), scopedToClients: false };

  const allowed = new Set(
    picked.flatMap((id) => {
      const patient = (allPatients || []).find((p) => p.id === id);
      return (patient?.facilityId || []).map(refId).filter(Boolean);
    })
  );
  return {
    options: active.filter((f) => allowed.has(f.id)).map((f) => ({ value: f.id, label: f.name })),
    scopedToClients: true,
  };
};

export const FacilityField = ({ value, onChange, clientIds, variant }) => {
  const cn = useSrFieldClassNames(variant);
  const { options, scopedToClients } = useScopedFacilities(clientIds);
  return (
    <Select
      label="Facility"
      required
      mb={14}
      classNames={cn}
      data={options}
      value={value || ""}
      searchable
      onChange={onChange}
      {...srCaretProps(variant)}
      placeholder={
        scopedToClients && !options.length ? "No shared facility for these clients" : "Select facility…"
      }
    />
  );
};

export const ShiftField = ({ value, onChange, variant }) => {
  const cn = useSrFieldClassNames(variant);
  return (
    <Select
      label="Shift"
      mb={14}
      clearable
      classNames={cn}
      placeholder="Select shift…"
      data={SHIFTS}
      value={value || ""}
      onChange={onChange}
      {...caretUnlessClearable(variant, value)}
    />
  );
};

export const DateField = ({ value, onChange, variant }) => {
  const cn = useSrFieldClassNames(variant);
  const { classes } = useFieldStyles();
  return (
    <DateInput
      label="Date"
      mb={14}
      clearable
      classNames={{
        ...cn,
        calendarHeader: classes.calHeader,
        calendarHeaderLevel: classes.calHeaderLevel,
        calendarHeaderControl: classes.calHeaderControl,
        weekday: classes.calWeekday,
        day: classes.calDay,
        monthCell: classes.calMonthCell,
        month: classes.calMonth,
      }}
      popoverProps={{ classNames: { dropdown: classes.dateDropdown }, width: "target" }}
      placeholder="Pick a date…"
      valueFormat="MMM D, YYYY"
      value={value || null}
      onChange={onChange}
      {...caretUnlessClearable(variant, value, LuCalendar)}
    />
  );
};

export const TimeField = ({ value, onChange, variant }) => {
  const cn = useSrFieldClassNames(variant);
  return (
    <TimeInput
      label="Time"
      mb={14}
      classNames={cn}
      value={value || ""}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
};

export const DescriptionField = ({ value, onChange }) => {
  const cn = useSrAreaClassNames(useSrFieldClassNames());
  return (
    <Textarea
      label="Description"
      required
      mb={14}
      autosize
      minRows={4}
      classNames={cn}
      placeholder="Describe the request…"
      value={value || ""}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
};

// Uploads straight to S3 via the presigned-URL flow (same pattern as
// ComplianceTabs/UploadStaffDoc) — onChange only ever receives an array of
// public URLs, never raw File objects, so callers just round-trip strings.
export const PhotosField = ({ value, onChange }) => {
  const cn = useSrFieldClassNames();
  const { classes } = useFieldStyles();
  const [uploading, setUploading] = useState(false);

  const handleDrop = async (files) => {
    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map((file) => uploadFileToS3Api({ file })));
      onChange([...(value || []), ...uploaded.map((r) => r.uploadURL || r.path)]);
    } catch (error) {
      showError(error?.message || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => onChange((value || []).filter((_, i) => i !== idx));

  return (
    <Box mb={14}>
      <Box component="label" className={cn.label} display="block">Upload Image</Box>
      <Dropzone onDrop={handleDrop} accept={IMAGE_MIME_TYPE} disabled={uploading} className={classes.photoDropzone}>
        <Box className={classes.dropInner}>
          <Box className={classes.dropIcon}>{uploading ? <Loader size={18} /> : <IconPhotoPlus size={20} />}</Box>
          <Text className={classes.dropMain}>
            {uploading ? "Uploading…" : <>Drag photos here, or <span className={classes.dropBrand}>browse files</span></>}
          </Text>
          <Text className={classes.dropSub}>PNG or JPG up to 10MB each</Text>
        </Box>
      </Dropzone>
      {!!(value || []).length && (
        <Box className={classes.photoGrid}>
          {value.map((url, i) => (
            <Box key={url} className={classes.photoThumbWrap}>
              <img src={url} alt="" className={classes.photoThumb} />
              <ActionIcon size="xs" className={classes.photoRemove} onClick={() => removeAt(i)} aria-label="Remove photo">
                <IconX size={11} />
              </ActionIcon>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Split out so useDirectories() (and its fetch effect) only runs for
// templates that actually have a Client/Staff/Facility field, instead of on
// every field OneField renders regardless of type.
const DirectoryField = ({ field, value, onChange, common, clientIds, variant }) => {
  const multiCn = useSrMultiClassNames(common.classNames, variant);
  const { allPatients, allStaff, allFacilities } = useDirectories();
  // A template's own Facility field narrows to the clients picked on the same
  // form, exactly as the built-in FacilityField narrows to the request's
  // clients — the rule is about the request, not about which form collected it.
  const { options: facilityOptions, scopedToClients } = useScopedFacilities(clientIds);
  // Same roster as the built-in StaffField — a template's own Staff field asks
  // the identical question, so it can't offer a different set of names.
  const options = field.fieldType === "Staff"
    ? allStaff.filter(isSelectableStaff)
        .map((s) => ({ value: String(s.id || s._id), label: staffName(s) }))
    : field.fieldType === "Facility"
      ? facilityOptions
      : allPatients.filter((p) => p?.active !== false && p?.deleted !== true).map((p) => ({ value: p.id, label: p.name }));

  // Drop a facility the current client set doesn't serve, so changing the
  // Client field can't leave a stale answer behind that submits against a site
  // the client isn't at. Held until both directories are actually loaded —
  // until then "not in the options" only means "not fetched yet", and this
  // renders in edit mode too, where clearing a saved answer would be wrong.
  const directoriesLoaded = allFacilities.length > 0 && allPatients.length > 0;
  const facilityInScope = facilityOptions.some((o) => o.value === refId(value));
  useEffect(() => {
    if (field.fieldType !== "Facility" || !value) return;
    if (!scopedToClients || !directoriesLoaded || facilityInScope) return;
    onChange(field.fieldId, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.fieldType, field.fieldId, value, scopedToClients, directoriesLoaded, facilityInScope]);
  // Client(s) and Staff pick as many as the request involves — the same as the
  // built-in ClientsField/StaffField the default request types use, so a
  // template-defined form is no more restrictive than the one it replaces.
  // Facility stays single: a request belongs to exactly one.
  // Requests filed while these were single-select hold a bare id, so the value
  // is normalised on the way in rather than fed to MultiSelect as a string.
  if (field.fieldType === "Facility") {
    return (
      <Select
        {...common}
        // Say why the list is empty rather than leaving the author's
        // placeholder inviting a pick that can't be made — same wording the
        // built-in FacilityField uses.
        placeholder={scopedToClients && !options.length ? "No shared facility for these clients" : common.placeholder}
        data={options}
        value={value || ""}
        searchable
        onChange={(v) => onChange(field.fieldId, v)}
        {...srCaretProps(variant)}
      />
    );
  }
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  return <MultiSelect {...common} classNames={multiCn} data={options} value={selected} searchable onChange={(v) => onChange(field.fieldId, v)} {...srCaretProps(variant)} />;
};

// Its own component so the categories are only selected/fetched for templates
// that actually carry an Issue Category field — same reason DirectoryField is
// split out above. `activeOnly` (the builder's "Active categories only"
// switch) decides whether retired categories still show.
const IssueCategoryPicker = ({ field, value, onChange, common, variant }) => {
  const options = useIssueCategoryOptions(field.activeOnly);
  return (
    <Select
      {...common}
      placeholder={field.placeholder || "Select a category…"}
      data={options}
      value={value || ""}
      searchable
      {...srCaretProps(variant)}
      onChange={(v) => onChange(field.fieldId, v)}
    />
  );
};

// Label + help chrome for the controls that aren't Mantine inputs (they can't
// take `label`/`description`/`required` props of their own).
const FieldShell = ({ cn, classes, label, help, required, children }) => (
  <Box mb={14}>
    <Box component="label" className={cn.label} display="block">
      {label}{required ? <span className={cn.required}> *</span> : null}
    </Box>
    {children}
    {help ? <Text className={classes.segHelp}>{help}</Text> : null}
  </Box>
);

// One row of toggle buttons, the shape the studio preview gives every choice
// field. `multiple` keeps a set instead of replacing the answer.
const SegmentedField = ({ field, options, value, onChange, multiple, cn, classes, cx }) => {
  const selected = multiple ? (Array.isArray(value) ? value : []) : value;
  const isOn = (v) => (multiple ? selected.includes(v) : selected === v);
  const pick = (v) => {
    if (!multiple) return onChange(field.fieldId, selected === v ? null : v);
    return onChange(field.fieldId, isOn(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };
  return (
    <FieldShell cn={cn} classes={classes} label={field.label} help={field.helpText} required={field.required}>
      <Box className={classes.seg}>
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={isOn(o.value)}
            className={cx(classes.segBtn, isOn(o.value) && classes.segBtnOn)}
            onClick={() => pick(o.value)}
          >
            {o.color ? <span className={classes.severityDot} style={{ background: o.color }} /> : null}
            {o.label}
          </button>
        ))}
      </Box>
    </FieldShell>
  );
};

const OneField = ({ field, value, onChange, clientIds, formFields, values, variant }) => {
  const cn = useSrFieldClassNames(variant);
  const areaCn = useSrAreaClassNames(cn);
  const { classes, cx } = useFieldStyles();
  const common = {
    label: field.label,
    description: field.helpText || undefined,
    placeholder: field.placeholder || undefined,
    required: field.required,
    mb: 14,
    classNames: cn,
  };

  switch (field.fieldType) {
    case "TextArea":
      return <Textarea {...common} classNames={areaCn} minRows={3} value={value || ""} onChange={(e) => onChange(field.fieldId, e.currentTarget.value)} />;
    case "Number":
      return <NumberInput {...common} value={value ?? ""} onChange={(v) => onChange(field.fieldId, v)} />;
    case "Date":
      return (
        <DateInput
          {...common}
          classNames={{
            ...cn,
            calendarHeader: classes.calHeader,
            calendarHeaderLevel: classes.calHeaderLevel,
            calendarHeaderControl: classes.calHeaderControl,
            weekday: classes.calWeekday,
            day: classes.calDay,
            monthCell: classes.calMonthCell,
            month: classes.calMonth,
          }}
          popoverProps={{ classNames: { dropdown: classes.dateDropdown }, width: "target" }}
          clearable
          value={value ? new Date(value) : null}
          onChange={(v) => onChange(field.fieldId, v)}
          {...caretUnlessClearable(variant, value, LuCalendar)}
        />
      );
    case "Time":
      return <TimeInput {...common} value={value || ""} onChange={(e) => onChange(field.fieldId, e.currentTarget.value)} />;
    // Dropdown is the one choice type the preview also shows as a select —
    // the rest are button rows there, so they are here too.
    case "Dropdown":
      return <Select {...common} data={field.options || []} value={value || ""} onChange={(v) => onChange(field.fieldId, v)} {...srCaretProps(variant)} />;
    case "SingleChoice":
      return (
        <SegmentedField
          field={field}
          options={(field.options || []).map((o) => ({ value: o, label: o }))}
          value={value}
          onChange={onChange}
          cn={cn}
          classes={classes}
          cx={cx}
        />
      );
    case "MultipleChoice":
      return (
        <SegmentedField
          field={field}
          options={(field.options || []).map((o) => ({ value: o, label: o }))}
          value={value}
          onChange={onChange}
          multiple
          cn={cn}
          classes={classes}
          cx={cx}
        />
      );
    case "YesNo":
      // Stored as a boolean, so the buttons carry booleans rather than the
      // author's chosen wording.
      return (
        <SegmentedField
          field={field}
          options={[{ value: false, label: field.noLabel || "No" }, { value: true, label: field.yesLabel || "Yes" }]}
          value={typeof value === "boolean" ? value : undefined}
          onChange={onChange}
          cn={cn}
          classes={classes}
          cx={cx}
        />
      );
    case "Severity":
      return (
        <SegmentedField
          field={field}
          options={(field.severityOptions || []).map((o) => ({ value: o.label, label: o.label, color: o.color }))}
          value={value}
          onChange={onChange}
          cn={cn}
          classes={classes}
          cx={cx}
        />
      );
    case "Client":
    case "Staff":
    case "Facility":
      return <DirectoryField field={field} value={value} onChange={onChange} common={common} clientIds={clientIds} variant={variant} />;
    case "IssueCategory":
      return <IssueCategoryPicker field={field} value={value} onChange={onChange} common={common} variant={variant} />;
    case "Notifications":
      // Builder-only metadata — never rendered as a submit-form input.
      return null;
    case "Calculation": {
      // Read-only by design — the server recomputes it on save and is the
      // authority on what gets stored. This runs the same arithmetic locally
      // so the submitter watches the total land as they type, rather than
      // staring at a promise that it'll be worked out later.
      const computed = evaluateFormula(field.formula, formFields, values);
      const text = computed != null
        ? String(Number(computed.toFixed(field.decimalPlaces ?? 2)))
        : (field.formula ? "—" : "Computed automatically on save");
      return (
        <FieldShell cn={cn} classes={classes} label={field.label} help={field.helpText} required={field.required}>
          <Box className={cx(classes.calcBox, computed == null && classes.calcBoxEmpty)}>{text}</Box>
        </FieldShell>
      );
    }
    case "CarePlan":
      return <TextInput {...common} disabled value="Attached from the client's active care plan" />;
    case "Text":
    default:
      return <TextInput {...common} value={value || ""} onChange={(e) => onChange(field.fieldId, e.currentTarget.value)} />;
  }
};

// Mirrors the builder's own live-preview evaluator (condOn/fieldHolds/
// ruleHolds in ServiceRequestTemplateBuilder/index.jsx) so a field or whole
// section hides on the real form under exactly the condition its author saw
// hide it on the canvas. The builder stores a rule's trigger by label
// (matching how its preview keys pv[]), so — like evaluateFormula's
// labelToId map — a rule's `field` is resolved back to a fieldId here to
// read the live fieldValues. Section triggers ("SEC::<id>") read that
// section's own fields for is-empty/has-response/is-complete, and that
// section's own persisted `cond` (see schema.js's sectionsPayload) for
// is-shown/is-hidden.
const conditionIsFilled = (v) => !(v == null || v === "" || (Array.isArray(v) && !v.length));

const conditionRuleHolds = (rule, ctx) => {
  const { fieldByLabel, values, sectionsById } = ctx;
  if (String(rule.field).startsWith("SEC::")) {
    const sectionId = rule.field.slice(5);
    const sectionFields = [...fieldByLabel.values()].filter((f) => f.sectionId === sectionId);
    const sectionValues = sectionFields.map((f) => values?.[f.fieldId]);
    switch (rule.op) {
      case "is empty": return !sectionValues.some(conditionIsFilled);
      case "has response": return sectionValues.some(conditionIsFilled);
      case "is complete":
        return sectionFields.filter((f) => f.required).every((f) => conditionIsFilled(values?.[f.fieldId]));
      case "is shown": return conditionHolds(sectionsById?.get(sectionId)?.cond, ctx);
      case "is hidden": return !conditionHolds(sectionsById?.get(sectionId)?.cond, ctx);
      default: return true;
    }
  }
  const target = fieldByLabel.get(rule.field);
  const raw = target ? values?.[target.fieldId] : undefined;
  // A YesNo field stores a real boolean (see OneField's "YesNo" case), but
  // the condition editor's rule value is always the string "Yes"/"No" —
  // matching the builder preview's own sandbox, which never touches the
  // author's custom yes/no wording either. Normalize to that same string
  // before comparing, or "is"/"is not" against a YesNo trigger can never
  // hold no matter what the submitter picks.
  const v = typeof raw === "boolean" ? (raw ? "Yes" : "No") : raw;
  switch (rule.op) {
    case "is": return v === rule.value;
    case "is not": return v !== rule.value;
    case "greater than": return Number(v) > Number(rule.value);
    case "less than": return Number(v) < Number(rule.value);
    case "contains": return String(v || "").toLowerCase().includes(String(rule.value || "").toLowerCase());
    case "includes": return Array.isArray(v) && v.includes(rule.value);
    case "does not include": return !(Array.isArray(v) && v.includes(rule.value));
    case "is empty": return !conditionIsFilled(v);
    case "is filled": case "is not empty": case "has response": return conditionIsFilled(v);
    default: return true;
  }
};

const conditionHolds = (conditional, ctx) => {
  if (!conditional?.rules?.length) return true;
  return conditional.mode === "any"
    ? conditional.rules.some((r) => conditionRuleHolds(r, ctx))
    : conditional.rules.every((r) => conditionRuleHolds(r, ctx));
};

// Builds the shared evaluation context once per render — callers use it both
// to filter a section's own fields (ServiceRequestFields below) and, at the
// drawer level, to decide whether an entire section card should render at
// all (see templateFormCards's `cond` and ServiceRequestNewDrawer).
export const buildConditionContext = ({ sections, formFields, values }) => ({
  fieldByLabel: new Map((formFields || []).map((f) => [f.label, f])),
  sectionsById: new Map((sections || []).map((s) => [s.sectionId, s])),
  values,
});

export { conditionHolds };

// Renders template.fields against a fieldValues map, reproducing the layout
// the template builder's canvas was designed with: `order` fixes the sequence
// and `span` the width, so a field the author sized to ½ arrives here at ½
// rather than at whatever its field type would suggest. Templates saved before
// widths persisted carry no span, and fall back to the same default the
// builder assigns on drop (see fieldSpan).
//
// When rendered inside a grid (e.g. the New Request drawer's .dgrid) each
// field is wrapped with the matching column span; callers that don't use a
// grid (e.g. DetailDrawer's flat edit-mode stack) are unaffected since the
// span classes are no-ops outside a grid container.
// `allFields` is every field on the form, where `fields` is only the subset
// being rendered here — the New Request drawer makes one call per section
// card, and a Facility narrowing to a Client field, or a Calculation
// referencing another field, may well be looking across that boundary.
const ServiceRequestFields = ({ fields, values, onChange, allFields, sections, variant }) => {
  const { classes } = useFieldStyles();
  const spanClass = { 4: classes.span4, 6: classes.span6, 12: classes.span12 };
  const formFields = allFields || fields || [];
  const conditionCtx = buildConditionContext({ sections, formFields, values });
  const sorted = (fields || [])
    .filter((field) => conditionHolds(field.conditional, conditionCtx))
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  // The clients this form has collected so far, gathered here rather than in
  // DirectoryField: a Facility field has to narrow to them (see
  // useScopedFacilities) but sits in the same flat fieldValues map as the
  // Client field(s) it depends on, with no way to reach across to them alone.
  const clientIds = formFields
    .filter((f) => f.fieldType === "Client")
    .flatMap((f) => {
      const v = values?.[f.fieldId];
      return (Array.isArray(v) ? v : [v]).filter(Boolean).map(refId);
    })
    .filter(Boolean);
  return sorted.map((field) => {
    // Respect the template author's own ⅓/½/Full width choice regardless of
    // where the form renders — the Detail drawer's narrower rail column
    // just means fewer fields end up sharing a row, not that every field
    // has to claim the full row on its own. Client/Staff/Facility are the
    // exception: they always render in that narrow rail (see
    // RAIL_TEMPLATE_FIELD_TYPES in the detail drawer) — a MultiSelect's
    // chip tray or a Select needs the full column regardless of whatever
    // width the author picked for it.
    const span = ["Client", "Staff", "Facility"].includes(field.fieldType)
      ? classes.span12
      : spanClass[fieldSpan(field)] || classes.span12;
    return (
      <Box key={field.fieldId} className={span}>
        <OneField
          field={field}
          value={values?.[field.fieldId]}
          onChange={onChange}
          clientIds={clientIds}
          formFields={formFields}
          values={values}
          variant={variant}
        />
      </Box>
    );
  });
};

export default ServiceRequestFields;
